/**
 * The gun in your hands.
 *
 * Drawn in its own pass, with its own camera and its own lights. A weapon is
 * not in the world — it is in front of your face — and trying to draw it with
 * the world camera means choosing between a gun that clips through the wall
 * you are standing next to and a gun whose own parts draw in the wrong order.
 * A second pass over a cleared depth buffer gets both: the weapon is always in
 * front of the level, and always correctly in front of itself.
 *
 * It also decouples the framing. The world is drawn at a wide field of view
 * because a shooter needs peripheral vision; a gun drawn at that field of view
 * is a stretched sliver in the corner. Every shooter solves this the same way,
 * with a narrower field of view for the weapon alone, and that is the single
 * biggest reason a gun looks like it has weight.
 *
 * Where the gun sits comes from the model: `weapons.manifest.json` measured
 * every one of them into the same pose — muzzle down -Z, sights up, origin at
 * the back of the receiver — so a pistol and an anti-materiel rifle can share
 * one rest position and both look right. The near end is always in the same
 * place and a longer gun simply reaches further into the screen.
 *
 * The animation is deliberately small and readable: a kick when it fires, a
 * dip out of frame and back when it reloads, and for the actions that need
 * working by hand — bolts, pumps, levers — a rock backward and forward between
 * shots. You should be able to tell what the gun is doing without the HUD.
 */

import * as THREE from 'three';
import type { RunState } from './run.ts';
import type { Player } from '../three/player.ts';
import { familyOf, type Weapon } from './weapons.ts';
import { GUN_BASE, GUN_BY_FILE, gripOf, gunLoader, markFamily, muzzleOf, poseGun } from './gunpose.ts';

export interface ViewModel {
  /**
   * Load a gun and compile it against this scene, before it is ever held.
   *
   * The view model is a second scene with its own lights, so a gun that has
   * been parsed and warmed for the world is still an uncompiled program here —
   * which is why picking one up cost a frame even once the file was cached.
   */
  prefetch(file: string | undefined): void;
  setWeapon(w: Weapon): void;
  update(dt: number, s: RunState, p: Player): void;
  /** Second pass, over a cleared depth buffer. Call after the world. */
  render(renderer: THREE.WebGLRenderer): void;
  resize(width: number, height: number): void;
}

/**
 * How wide the weapon is drawn, as a horizontal field of view in degrees.
 *
 * Held constant across shapes of screen: a phone in portrait has a fraction of
 * the horizontal field a laptop does, and matching the vertical field instead
 * would put the same gun across the whole width. Matching the horizontal one
 * keeps the weapon the same fraction of the width everywhere, which is the
 * dimension it is long in.
 */
const GUN_HFOV = 68;

/**
 * Where the back of the receiver sits for a full-length rifle, in metres.
 *
 * Every gun is anchored at the back of the receiver, so this is the one point
 * they all share. A pistol anchored there would be entirely in the corner
 * though — there is only twenty centimetres of it to reach into the frame —
 * so short weapons are held closer and further inboard, which is what a real
 * shooter's per-weapon view model offsets are doing too.
 */
const REST = new THREE.Vector3(0.135, -0.125, -0.36);
const REST_LENGTH = 0.9;

/**
 * How quickly the weapon eases back to where it was, per second.
 *
 * Fast enough that the gun is home before a rifle's next round, so a burst
 * reads as a series of separate punches rather than one long push.
 */
const RETURN = 16;

/** Actions you have to work by hand between rounds. */
const CYCLED = new Set(['bolt', 'pump', 'lever', 'breech']);

const cache = new Map<string, Promise<THREE.Object3D | null>>();

/**
 * Load a model and put it in the pose the manifest measured for it.
 *
 * One instance per file, used rather than cloned: `Object3D.clone` does not
 * rebind a skeleton, and every one of these exports is rigged, so a clone
 * renders nothing at all while still reporting the right bounds. Only one
 * weapon is ever on screen, so a single instance per file is all that is
 * needed anyway.
 */
/**
 * Start fetching and parsing a gun before anybody needs it.
 *
 * Walking onto a weapon used to cost a hundred and forty milliseconds, because
 * that is the moment its file was fetched, parsed, skinning-baked and posed —
 * one frame, right as the thing you came for is being handed to you. A pickup
 * is visible for seconds before you reach it and the result is cached by file,
 * so the work can simply happen then instead. Nothing waits on this: if it has
 * not finished by the time you pick the gun up, the ordinary path finds the
 * same promise already in flight and the wait is whatever is left of it.
 */
function loadModel(file: string): Promise<THREE.Object3D | null> {
  const hit = cache.get(file);
  if (hit) return hit;
  const entry = GUN_BY_FILE.get(file);
  const job = new Promise<THREE.Object3D | null>((resolve) => {
    if (!entry) {
      resolve(null);
      return;
    }
    gunLoader.load(
      GUN_BASE + file,
      (gltf) => {
        const posed = poseGun(gltf.scene, entry);
        /**
         * Keep the model's own materials, minus the parts that break it.
         *
         * These exports ship `MeshPhysicalMaterial` with the specular and
         * transmission extensions. Transmission draws in a separate pass
         * against a render target this scene never sets up, and it costs far
         * more than a gun in the corner of the screen is worth on a phone. So
         * the material is rebuilt as a standard one — but everything that
         * makes the gun look like itself is carried across: its colour, its
         * texture maps, its roughness and metalness. An earlier version of
         * this flattened them to unlit and lifted them off black, which made
         * every weapon look like the same grey plastic.
         */
        posed.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          // A weapon is never out of frame, and its bounds are wrong anyway
          // while the skeleton is what actually places the vertices.
          m.frustumCulled = false;
          const src = Array.isArray(m.material) ? m.material : [m.material];
          const rebuilt = src.map((one) => {
            const from = one as THREE.MeshStandardMaterial;
            const colour = from?.color ? from.color.clone() : new THREE.Color(0x8a9099);
            return new THREE.MeshStandardMaterial({
              color: colour,
              map: from?.map ?? null,
              normalMap: from?.normalMap ?? null,
              roughnessMap: from?.roughnessMap ?? null,
              metalnessMap: from?.metalnessMap ?? null,
              aoMap: from?.aoMap ?? null,
              roughness: from?.roughness ?? 0.55,
              metalness: from?.metalness ?? 0.4,
              // A trace of self-illumination so a black receiver keeps an edge
              // against a dark wall, without washing the finish out.
              emissive: colour.clone().multiplyScalar(0.1),
              side: THREE.FrontSide,
            });
          });
          m.material = Array.isArray(m.material) ? rebuilt : rebuilt[0];
          for (const one of src) (one as THREE.Material)?.dispose?.();
        });
        resolve(posed);
      },
      undefined,
      () => resolve(null),
    );
  });
  cache.set(file, job);
  return job;
}

/** Rough proportions per class, for the block stand-in shown while loading. */
const STANDIN: Record<string, [number, number]> = {
  pistol: [0.21, 0.14], revolver: [0.25, 0.15], machine_pistol: [0.3, 0.19],
  smg: [0.48, 0.22], pdw: [0.42, 0.2], shotgun: [0.98, 0.24], carbine: [0.76, 0.25],
  rifle: [0.9, 0.26], battle: [1.02, 0.26], dmr: [1.1, 0.28], sniper: [1.24, 0.3],
  antimat: [1.45, 0.34], lmg: [1.1, 0.32], launcher: [0.72, 0.26], bow: [0.9, 0.7],
  special: [0.8, 0.26],
};

export function viewModel(): ViewModel {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(50, 1.6, 0.02, 6);

  // Its own light rig, so a weapon looks the same in a sunlit courtyard and an
  // unlit basement. Nothing else is in this scene, so nothing else sees them.
  const key = new THREE.PointLight(0xfff2dc, 5.4, 4.5, 2);
  key.position.set(0.55, 0.5, 0.15);
  const fill = new THREE.PointLight(0xa9bcd4, 1.7, 4.0, 2);
  fill.position.set(-0.5, -0.15, -0.4);
  const back = new THREE.PointLight(0xffffff, 1.7, 3.4, 2);
  back.position.set(0.1, 0.35, -1.2);
  scene.add(key, fill, back, new THREE.AmbientLight(0x3c424c, 1.1));

  /** Driven by the animation; everything visible hangs off it. */
  const gun = new THREE.Group();
  scene.add(gun);

  /** The block stand-in, shown until a model arrives — and if none ever does. */
  const blocks = new THREE.Group();
  const bodyMat = new THREE.MeshStandardMaterial({ color: 0x8b93a0, roughness: 0.5, metalness: 0.35 });
  const barrelMat = new THREE.MeshStandardMaterial({ color: 0x5f6672, roughness: 0.35, metalness: 0.6 });
  const gripMat = new THREE.MeshStandardMaterial({ color: 0x3c4048, roughness: 0.8 });
  const body = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), bodyMat);
  const barrel = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), barrelMat);
  const grip = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), gripMat);
  blocks.add(body, barrel, grip);
  gun.add(blocks);

  /** Whatever model is currently loaded, if any. */
  let model: THREE.Object3D | null = null;

  /*
   * Brass.
   *
   * The cheapest thing in a shooter that makes a gun feel like a machine
   * rather than a prop: something physical leaves it every time it fires. In
   * view space rather than world space, which is what most shooters do — the
   * cases follow the camera, nobody has ever noticed, and it costs no
   * transform back and forth.
   */
  const SHELLS = 14;
  const shellGeo = new THREE.BoxGeometry(0.012, 0.012, 0.032);
  /*
   * Graphite, not brass.
   *
   * A warm yellow case is the correct colour for a real one and the wrong
   * colour for this world: it is a saturated hue that nothing else here is
   * entitled to, thrown two a second, straight across the middle of the frame.
   * Dark and machined, like everything else made of metal.
   */
  const shellMat = new THREE.MeshStandardMaterial({ color: 0x2b333b, roughness: 0.3, metalness: 0.75 });
  const shells: THREE.Mesh[] = [];
  const shellVel: THREE.Vector3[] = [];
  const shellSpin: THREE.Vector3[] = [];
  const shellLife: number[] = [];
  for (let i = 0; i < SHELLS; i++) {
    const m = new THREE.Mesh(shellGeo, shellMat);
    m.visible = false;
    m.frustumCulled = false;
    scene.add(m);
    shells.push(m);
    shellVel.push(new THREE.Vector3());
    shellSpin.push(new THREE.Vector3());
    shellLife.push(0);
  }
  let nextShell = 0;

  /** A wisp off the muzzle once the barrel has been worked. */
  const SMOKE = 8;
  const smokeGeo = new THREE.PlaneGeometry(1, 1);
  const smokeMat = new THREE.MeshBasicMaterial({
    color: 0xb9bec6, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide,
  });
  const smoke: THREE.Mesh[] = [];
  const smokeLife: number[] = [];
  const smokeVel: THREE.Vector3[] = [];
  for (let i = 0; i < SMOKE; i++) {
    const m = new THREE.Mesh(smokeGeo, smokeMat.clone());
    m.visible = false;
    m.frustumCulled = false;
    scene.add(m);
    smoke.push(m);
    smokeLife.push(0);
    smokeVel.push(new THREE.Vector3());
  }
  let nextSmoke = 0;
  /**
   * Where this gun's muzzle and firing hand are, in the view model's frame.
   *
   * Kept as the weapon is set rather than derived from `length` at each use:
   * the smoke used to come off the front of the bounding box and the brass out
   * of a point a third of the way along it, which is the receiver on a rifle
   * and the middle of the barrel on a revolver.
   */
  const tip = new THREE.Vector3();
  const hand = new THREE.Vector3();

  /** How hot the barrel is, 0..1. Rises with fire, falls with time. */
  let heat = 0;

  const flashMat = new THREE.MeshBasicMaterial({ color: 0xffc271, transparent: true, depthWrite: false, toneMapped: false });
  const flash = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.14, 7), flashMat);
  flash.rotation.x = -Math.PI / 2;
  flash.visible = false;
  gun.add(flash);
  // A muzzle flash is a light before it is a shape: without this the gun does
  // not brighten when it fires and the shot reads as a decal on the screen.
  const flashLight = new THREE.PointLight(0xffb85c, 0, 1.6, 2);
  gun.add(flashLight);

  let current: Weapon | null = null;
  let length = 0.9;
  const rest = REST.clone();
  let token = 0;

  let recoil = 0;
  /**
   * How far back along its own axis the weapon is, 0..1.
   *
   * Separate from `recoil`, which is the muzzle flash's clock and has to be
   * over in forty milliseconds. This is the part you feel: the gun jumps
   * straight back on the frame the round leaves and eases forward to where it
   * was. Straight back and nowhere else — a view model that pitches its muzzle
   * up on every shot is drawing the recoil twice, once here and once in the
   * camera that is already climbing, and the two of them together are why a
   * burst looks like the gun is trying to escape your hands.
   */
  let travel = 0;
  let bob = 0;
  let lastAmmo = -1;
  let reloadTilt = 0;
  /** 0..1 through a hand-cycle of the action. */
  let cycle = 0;
  let cycling = false;
  let cycleTime = 0.42;

  function setWeapon(w: Weapon): void {
    current = w;
    const spec = STANDIN[w.cls] ?? STANDIN.rifle;
    length = w.modelLength ?? spec[0];
    const height = w.entry?.height ?? spec[1];

    // The stand-in is built to the same measurements as the model it is
    // standing in for, in the same pose, so the swap is not a jump.
    body.scale.set(height * 0.34, height * 0.44, length * 0.62);
    body.position.set(0, 0, -length * 0.34);
    barrel.scale.set(height * 0.17, height * 0.17, length * 0.42);
    barrel.position.set(0, height * 0.06, -length * 0.78);
    grip.scale.set(height * 0.3, height * 0.62, length * 0.16);
    grip.position.set(0, -height * 0.42, -length * 0.16);
    bodyMat.color.setHex(w.colour).multiplyScalar(0.5).addScalar(0.16);
    barrelMat.color.setHex(w.colour).multiplyScalar(0.28).addScalar(0.1);

    // Short guns come closer and inboard so there is something to look at.
    const reach = THREE.MathUtils.clamp(length / REST_LENGTH, 0.34, 1.6);
    rest.set(
      REST.x * (0.56 + 0.44 * reach),
      REST.y * (0.62 + 0.38 * reach),
      REST.z * (0.62 + 0.38 * reach),
    );

    /*
     * The flash comes out of the barrel, not out of the bounding box.
     *
     * This was the middle of the front face for every gun in the library, so
     * a revolver flashed from the end of its cylinder frame, anything with a
     * scope or a folded bipod reaching past the bore flashed off that instead,
     * and a gun whose barrel sits low flashed a few centimetres above the
     * hole. The point is measured per gun now, and a hand correction wins over
     * the measurement.
     */
    tip.copy(w.entry ? muzzleOf(w.entry) : new THREE.Vector3(0, 0, -length - 0.03));
    hand.copy(w.entry ? gripOf(w.entry) : new THREE.Vector3(0.012, -0.035, -length * 0.22));
    flash.position.copy(tip);
    // The light sits just behind the tip so the barrel itself is lit rather
    // than a point hanging in front of it.
    flashLight.position.set(tip.x, tip.y, tip.z * 0.9);
    lastAmmo = -1;
    cycle = 0;
    cycling = false;

    // Swap the previous model out immediately; the new one arrives when it does.
    if (model) {
      gun.remove(model);
      model = null;
    }
    blocks.visible = true;
    const mine = ++token;
    if (w.model) {
      void loadModel(w.model).then((obj) => {
        // A late arrival for a gun you already swapped away from is dropped.
        if (mine !== token || !obj) return;
        // A model can only be in one place at a time, and the ground pickups
        // parse their own copies, so nothing else can be holding this one.
        if (obj.parent) obj.parent.remove(obj);
        model = obj;
        markFamily(model, familyOf(w.cls).colour);
        gun.add(model);
        blocks.visible = false;
      });
    }
  }

  /**
   * Throw a case out of the port.
   *
   * Up and to the right, tumbling, with enough spread that two shots never
   * look like the same shot. The port is on the right of the receiver about a
   * third of the way along, which is where it is on most of these guns and
   * close enough on the rest.
   */
  function eject(): void {
    if (!current || current.action === 'charge' || current.cls === 'bow') return;
    const i = nextShell;
    nextShell = (nextShell + 1) % SHELLS;
    const m = shells[i];
    m.visible = true;
    // Brass leaves the breech, which is a little ahead of and above the hand
    // on the grip — not a fixed fraction of the model's length.
    m.position.copy(gun.position);
    m.position.x += 0.05;
    m.position.y += hand.y + 0.09;
    m.position.z += hand.z - 0.05;
    m.rotation.set(Math.random() * 3, Math.random() * 3, Math.random() * 3);
    shellVel[i].set(0.55 + Math.random() * 0.5, 0.85 + Math.random() * 0.4, 0.35 + Math.random() * 0.3);
    shellSpin[i].set(
      (Math.random() - 0.5) * 26,
      (Math.random() - 0.5) * 26,
      (Math.random() - 0.5) * 26,
    );
    shellLife[i] = 0.85;
  }

  /** A wisp off the muzzle, and only once the barrel has had some use. */
  function puff(): void {
    const i = nextSmoke;
    nextSmoke = (nextSmoke + 1) % SMOKE;
    const m = smoke[i];
    m.visible = true;
    m.position.set(gun.position.x + tip.x, gun.position.y + tip.y + 0.01, gun.position.z + tip.z + 0.02);
    m.scale.setScalar(0.03);
    m.rotation.z = Math.random() * Math.PI;
    smokeVel[i].set((Math.random() - 0.5) * 0.12, 0.16 + Math.random() * 0.1, -0.05 - Math.random() * 0.08);
    smokeLife[i] = 0.9 + Math.random() * 0.5;
  }

  function debris(dt: number): void {
    for (let i = 0; i < SHELLS; i++) {
      if (shellLife[i] <= 0) continue;
      shellLife[i] -= dt;
      if (shellLife[i] <= 0) {
        shells[i].visible = false;
        continue;
      }
      shellVel[i].y -= 3.2 * dt;
      shells[i].position.addScaledVector(shellVel[i], dt);
      shells[i].rotation.x += shellSpin[i].x * dt;
      shells[i].rotation.y += shellSpin[i].y * dt;
      shells[i].rotation.z += shellSpin[i].z * dt;
    }

    // Smoke only above the point where the barrel would actually be warm, and
    // never more than a wisp: a gun that fogs the screen is a gun you cannot
    // shoot past.
    heat = Math.max(0, heat - dt * 0.42);
    if (heat > 0.45 && Math.random() < dt * 9 * heat) puff();
    for (let i = 0; i < SMOKE; i++) {
      if (smokeLife[i] <= 0) continue;
      smokeLife[i] -= dt;
      const m = smoke[i];
      const mat = m.material as THREE.MeshBasicMaterial;
      if (smokeLife[i] <= 0) {
        m.visible = false;
        mat.opacity = 0;
        continue;
      }
      m.position.addScaledVector(smokeVel[i], dt);
      m.scale.setScalar(0.03 + (1.4 - smokeLife[i]) * 0.14);
      mat.opacity = Math.min(0.16, smokeLife[i] * 0.14) * heat;
      m.rotation.z += dt * 0.4;
    }
  }

  function resize(width: number, height: number): void {
    const aspect = Math.max(0.2, width / Math.max(1, height));
    camera.aspect = aspect;
    // Hold the horizontal field: the vertical one falls out of the shape of
    // the screen. On a phone held upright that makes the vertical field very
    // wide, which is exactly right — the gun stays the same size and simply
    // sits lower in a taller frame.
    const half = THREE.MathUtils.degToRad(GUN_HFOV) / 2;
    camera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(half) / aspect));
    camera.updateProjectionMatrix();
  }
  resize(innerWidth, innerHeight);

  function update(dt: number, s: RunState, p: Player): void {
    if (s.ammo < lastAmmo) {
      recoil = 1;
      travel = 1;
      flash.visible = true;
      eject();
      heat = Math.min(1, heat + 0.16);
      flash.rotation.z = Math.random() * Math.PI;
      flash.scale.setScalar(0.85 + Math.random() * 0.4);
      // Anything worked by hand starts its cycle the moment the round leaves.
      if (current && CYCLED.has(current.action)) {
        cycling = true;
        cycle = 0;
        // Work the action over the time the weapon actually takes between
        // rounds, so the hand and the gun agree. A fixed duration reads as
        // wrong on both a fast lever and a slow bolt.
        cycleTime = Math.max(0.22, Math.min(0.9, current.interval * 0.75));
      }
    } else if (recoil < 0.55) {
      flash.visible = false;
    }
    lastAmmo = s.ammo;
    recoil = Math.max(0, recoil - dt * 9);
    // Framerate-independent ease home. A fixed subtraction per frame is a
    // ramp, and a ramp arriving at zero stops dead; this arrives slowing down,
    // which is what a spring returning to rest does.
    travel += (0 - travel) * (1 - Math.exp(-RETURN * dt));
    flashMat.opacity = Math.min(1, recoil * 1.4);
    flashLight.intensity = recoil * recoil * 9;

    /*
     * Walking moves the weapon, but not much.
     *
     * The bob was a third again as fast and nearly twice as far, and at a
     * sprint it read as the gun being carried by somebody jogging rather than
     * held by somebody advancing — the whole frame pumped, which is tiring to
     * look at and makes a moving shot feel less aimed than it is. Slower, and
     * with the vertical component cut hardest, because up-and-down is the one
     * the eye tracks.
     *
     * `sway` also stops climbing sooner: the difference between walking and
     * sprinting should be a little more movement, not a lot, and dividing by
     * seven rather than five means a sprint sits at about two thirds rather
     * than pinned at the maximum.
     */
    const speed = Math.hypot(p.velocity.x, p.velocity.z);
    bob += dt * (2.9 + speed * 1.05);
    const sway = Math.min(1, speed / 7);

    reloadTilt += ((s.reloading ? 1 : 0) - reloadTilt) * Math.min(1, dt * 9);

    // Working the action: rock the muzzle up and back down, the shape of a
    // pump or a bolt throw seen from behind the gun.
    let cyclePitch = 0;
    let cycleBack = 0;
    if (cycling) {
      cycle += dt / cycleTime;
      if (cycle >= 1) {
        cycle = 0;
        cycling = false;
      } else {
        const t = cycle;
        // Out and back: fast away, slower returning, like a hand does it.
        const swing = t < 0.45 ? t / 0.45 : 1 - (t - 0.45) / 0.55;
        cyclePitch = swing * 0.42;
        cycleBack = swing * 0.08;
      }
    }

    // Heavier guns move less and settle slower — the only cue the view model
    // has for weight, and it is worth more than any amount of detail.
    const heft = Math.min(1.4, Math.max(0.5, length / 0.9));
    gun.position.set(
      rest.x + Math.sin(bob) * 0.006 * sway / heft,
      rest.y + Math.abs(Math.cos(bob)) * 0.005 * sway / heft - reloadTilt * 0.26,
      rest.z + travel * 0.075 * heft + cycleBack,
    );
    // A few degrees of yaw and roll so the weapon is seen slightly from the
    // side rather than dead-on, which is how every shooter frames it — a gun
    // pointing exactly down the crosshair reads as a plank.
    debris(dt);
    gun.rotation.set(
      -reloadTilt * 0.85 + cyclePitch,
      0.115 + Math.sin(bob * 0.5) * 0.010 * sway - reloadTilt * 0.3,
      0.045 - reloadTilt * 0.5,
    );
  }

  // A handle for checking, from the console, that exactly one weapon is in
  // there — the failure that put two guns in your hands at once was invisible
  // from the outside because both of them looked plausible.
  if (import.meta.env?.DEV) (globalThis as Record<string, unknown>).__vm = { scene, camera, gun };

  /*
   * Guns waiting to be compiled against this scene.
   *
   * Parked a kilometre away and left visible: `renderer.compile` walks the
   * visible graph, so it has to be in the scene and not hidden, and the only
   * way to have both without a second gun appearing in shot is to put it
   * somewhere the camera will never point.
   */
  const warming: THREE.Object3D[] = [];

  function prefetch(file: string | undefined): void {
    // Nothing to warm where there is no renderer: the headless tools build
    // runs and drop weapons without ever fetching a model, and a promise
    // rejecting into an empty page is an unhandled rejection with no owner.
    if (!file || typeof document === 'undefined') return;
    void loadModel(file)
      .then((obj) => {
        if (!obj || obj.parent) return;
        const stand = new THREE.Group();
        stand.position.set(0, -1000, 0);
        stand.add(obj);
        scene.add(stand);
        warming.push(stand);
      })
      .catch(() => null);
  }

  function render(renderer: THREE.WebGLRenderer): void {
    /*
     * One gun per frame, never two.
     *
     * Compiling everything parked in a single pass just moves the whole stall
     * to a different frame — four guns landing together cost a third of a
     * second in one go. Taking one at a time turns that into four ordinary
     * frames, and there is no hurry: the gun being warmed is lying on the
     * floor across the room.
     */
    const stand = warming.shift();
    if (stand) {
      renderer.compile(scene, camera);
      const model = stand.children[0];
      if (model) stand.remove(model);
      scene.remove(stand);
    }
    const auto = renderer.autoClear;
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(scene, camera);
    renderer.autoClear = auto;
  }

  return { setWeapon, prefetch, update, render, resize };
}
